import { getCookie } from "cookies-next";
import { useIntl } from "react-intl";

import { Button } from "@/components/atoms/Button";
import {
  buildPriceSummary,
  PriceSummary,
  type PriceFacts,
  type PriceSummaryProps,
  PriceSummarySections,
  type PriceSummarySection,
  PriceSummaryTitle,
  type PriceView,
  RowIds,
} from "@/components/molecules/PriceSummary";
import analyticsIntegrations from "~/app/analytics/analytics-client";
import { ListingData, useListingData } from "~/context/ListingPageDataContext";
import { AppModalKey, useAppModal } from "~/modals";
import type { BookListingEstimate, BookListingQuote } from "~/types/api-types";
import { ACCESS_TOKEN_COOKIE } from "~/utils/constants";

import { useBookListingContext } from "../../BookListingContext";
import { useNestProcessingFeeExperiment } from "../../hooks/use-nest-processing-fee-experiment";

import {
  buildBookListingPriceView,
  getBookListingPriceFacts,
  getBookListingPriceSource,
  toNestedFeePriceFacts,
} from "./BookListingPriceSummary.utils";

export type PriceBreakdown = {
  currency: string;
  facts: PriceFacts;
  sections: PriceSummarySection[];
};

type BookListingPriceSummaryProps = Pick<PriceSummaryProps, "title"> & {
  /**
   * Mobile only: called instead of opening `ListingPriceDetailsModal`, so
   * the caller — already inside its own `Drawer` — can show the breakdown as
   * a further step in that same sheet rather than a second, disconnected
   * `Drawer`/`DrawerManagerProvider`. Desktop has no such caller and keeps
   * opening `ListingPriceDetailsModal`.
   */
  onViewPriceBreakdown?: (breakdown: PriceBreakdown) => void;
};

type BookListingPriceSummaryContentProps = BookListingPriceSummaryProps & {
  estimate: BookListingEstimate | undefined;
  isFetchingQuote: boolean;
  listingData: ListingData;
  quote: BookListingQuote | undefined;
};

/**
 * `BookListingPriceSummary` with the listing and its quote passed in, for a caller
 * outside the listing page's providers: `ListingPriceDetailsDrawer` is rendered by
 * `AppModalsProvider`, above them.
 */
export const BookListingPriceSummaryContent = ({
  estimate,
  isFetchingQuote,
  listingData,
  onViewPriceBreakdown,
  quote,
  title,
}: BookListingPriceSummaryContentProps) => {
  const intl = useIntl();
  const { openModal: openPricingRulesModal } = useAppModal(
    AppModalKey.ListingPricingRulesModal,
  );
  const { openModal: openPriceDetailsModal } = useAppModal(
    AppModalKey.ListingPriceDetailsModal,
  );

  const source = getBookListingPriceSource({ estimate, quote });
  const view = buildBookListingPriceView({
    listing: listingData,
    quote,
  });

  // Called unconditionally, before any early return — its eligibility inputs
  // come from `listingData`/`source` rather than `facts`, which doesn't exist
  // yet at this point in the render.
  const isDiy = listingData.fullService?.active !== true;
  const hasQuote = source?.type === "quote";
  const { shouldNestFees } = useNestProcessingFeeExperiment({
    isDiy,
    hasQuote,
  });

  if (isFetchingQuote) {
    return <PriceSummary showSkeleton={true} title={title} />;
  }

  if (!source) {
    return null;
  }

  const facts = getBookListingPriceFacts({
    listing: listingData,
    source,
    view,
  });
  // Eligible DIY quotes always use the nested-fee facts.
  const isNestedFeeEligible = isDiy && hasQuote;
  const finalFacts = isNestedFeeEligible
    ? toNestedFeePriceFacts(facts, quote)
    : facts;
  // `nest_processing_fee_test_v2` treatment: without `Stages.PreCheckout`, DS
  // renders the DIY row as "Space rental for x hours" (DiySpaceRental) instead
  // of "rate x hours" (DiySpaceRentalRate). No other DS rule special-cases
  // PreCheckout. `PriceView` requires a stage, hence the cast.
  const summaryView = shouldNestFees
    ? ({ ...view, stage: undefined } as unknown as PriceView)
    : view;
  const model = buildPriceSummary(finalFacts, summaryView);
  const accessToken = getCookie(ACCESS_TOKEN_COOKIE) as string | undefined;
  const booking = quote?.spaceRentalContract?.meta.booking;
  const hourlyRate =
    listingData.prices.find((price) => price.type === "HOURLY_RATE")?.value ??
    0;
  // The itemized breakdown behind the blended row uses the real, un-nested
  // facts/sections — computed once here (only when fees are nested) so both
  // the click handler below and the desktop-only `ListingPriceDetailsModal`
  // share it.
  const breakdown: PriceBreakdown | null = isNestedFeeEligible
    ? {
        currency: listingData.listingCurrency,
        facts,
        sections: buildPriceSummary(facts, view).sections,
      }
    : null;
  const breakdownOverride = breakdown
    ? {
        onClick: () => {
          analyticsIntegrations.dialogInteraction({
            type: "modal",
            action: "open",
            name: "price summary modal",
            custom: { source: "nested space rental line item" },
          });
          if (onViewPriceBreakdown) {
            onViewPriceBreakdown(breakdown);
          } else {
            openPriceDetailsModal(breakdown);
          }
        },
      }
    : null;

  return (
    <>
      {title && <PriceSummaryTitle>{title}</PriceSummaryTitle>}
      <PriceSummarySections
        currency={listingData.listingCurrency}
        facts={finalFacts}
        intl={intl}
        sections={model.sections}
        testId="booking-listing-price-summary"
        renderPricingRulesLink={
          accessToken
            ? (linkLabel) => (
                <Button
                  className="tw-body-1-underline"
                  variant="text-only"
                  // Leaves the tooltip open on purpose. On mobile it renders as
                  // a stacked drawer, and closing it programmatically never
                  // fires vaul's open-change callback, so it would stay on the
                  // DrawerManager stack and keep the price drawer hidden.
                  onClick={() =>
                    openPricingRulesModal({
                      accessToken,
                      baseHourlyRate: hourlyRate,
                      currency: listingData.listingCurrency,
                      listingId: listingData.listingId,
                      pricingRules: booking?.rate_details,
                    })
                  }
                >
                  {linkLabel}
                </Button>
              )
            : undefined
        }
        tooltipOverrides={
          breakdownOverride
            ? {
                [RowIds.DiySpaceRentalRate]: breakdownOverride,
                ...(shouldNestFees && {
                  [RowIds.DiySpaceRental]: breakdownOverride,
                }),
              }
            : undefined
        }
      />
    </>
  );
};

/**
 * Renders the design-system price summary for the active listing estimate or
 * quote, opening the pricing rules and price details modals from its rows.
 */
export const BookListingPriceSummary = (
  props: BookListingPriceSummaryProps,
) => {
  const { data: listingData = {} as ListingData } = useListingData();
  const { quote, estimate, isFetchingQuote } = useBookListingContext();

  return (
    <BookListingPriceSummaryContent
      {...props}
      estimate={estimate}
      isFetchingQuote={isFetchingQuote}
      listingData={listingData}
      quote={quote}
    />
  );
};
